<template>
  <div v-if="backUrl" class="back-button-wrapper">
    <button class="back-button" @click="handleBack">
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        stroke-width="2" 
        stroke-linecap="round" 
        stroke-linejoin="round"
      >
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      <span>返回</span>
    </button>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const backUrl = ref('');

// 存储key
const STORAGE_KEY = 'app_back_url';

// 验证URL是否为有效的HTTP/HTTPS链接
const isValidUrl = (url) => {
  if (!url) return false;
  
  // 防止JavaScript伪协议和其他不安全的协议
  const lowerUrl = url.toLowerCase().trim();
  if (lowerUrl.startsWith('javascript:') || 
      lowerUrl.startsWith('data:') || 
      lowerUrl.startsWith('vbscript:')) {
    return false;
  }
  
  // 只允许HTTP和HTTPS协议
  return lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://');
};

// 从URL参数或sessionStorage加载返回URL
const loadBackUrl = () => {
  // 优先从URL query参数读取
  let url = route.query.backUrl || route.query.backurl;
  
  if (url) {
    try {
      // 解码URL（防止URL被编码）
      url = decodeURIComponent(url);
      
      // 验证URL有效性
      if (isValidUrl(url)) {
        backUrl.value = url;
        // 保存到sessionStorage，以便切换页面时保持
        sessionStorage.setItem(STORAGE_KEY, url);
        return;
      } else {
        console.warn('无效的返回URL:', url);
      }
    } catch (error) {
      console.error('解析返回URL失败:', error);
    }
  }
  
  // 如果URL参数中没有，尝试从sessionStorage读取
  const savedUrl = sessionStorage.getItem(STORAGE_KEY);
  if (savedUrl && isValidUrl(savedUrl)) {
    backUrl.value = savedUrl;
  }
};

// 初始化时加载
onMounted(() => {
  loadBackUrl();
});

// 监听路由变化，重新检查URL参数
watch(() => route.query, () => {
  loadBackUrl();
}, { deep: true });

// 处理返回按钮点击
const handleBack = () => {
  if (backUrl.value) {
    // 清除保存的返回URL
    sessionStorage.removeItem(STORAGE_KEY);
    window.location.href = backUrl.value;
  }
};
</script>

<style scoped>
.back-button-wrapper {
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 9999;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.back-button:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.back-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.back-button svg {
  flex-shrink: 0;
}

.back-button span {
  white-space: nowrap;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .back-button-wrapper {
    top: 12px;
    left: 12px;
  }
  
  .back-button {
    padding: 8px 14px;
    font-size: 13px;
    gap: 5px;
  }
  
  .back-button svg {
    width: 18px;
    height: 18px;
  }
}

/* 小屏幕优化 */
@media (max-width: 480px) {
  .back-button-wrapper {
    top: 10px;
    left: 10px;
  }
  
  .back-button {
    padding: 7px 12px;
    font-size: 12px;
    border-radius: 6px;
  }
  
  .back-button svg {
    width: 16px;
    height: 16px;
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .back-button {
    background: rgba(40, 40, 40, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
  
  .back-button:hover {
    background: rgba(50, 50, 50, 1);
  }
}
</style>

